import { useMemo } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import {
  Bar,
  CartesianGrid,
  ComposedChart as RechartsComposedChart,
  Label,
  Line,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { ChartAnnotationFlag } from '../ChartAnnotationFlag'
import { ChartFrame } from '../ChartFrame'
import { useChartSeries } from '../ChartSeriesContext'
import { ChartTooltipView } from '../ChartTooltipView'
import { ComparisonHatchBar } from '../comparisonHatch'
import {
  axisTickLabels,
  baseKey,
  buildSeriesRows,
  CHART_MARGIN,
  computeYAxisWidth,
  computeYDomain,
  DEFAULT_X_AXIS_HEIGHT,
  fitToGrid,
  snapToBucket,
  TICK_FONT_SIZE,
  useChartChrome,
  X_AXIS_MIN_TICK_GAP,
  X_AXIS_PADDING,
  Y_AXIS_LABEL_OFFSET,
  Y_AXIS_TICK_MARGIN,
  Y_AXIS_WIDTH,
} from '../cartesianChart'
import type { BucketGranularity } from '../bucketSeries'
import {
  formatBucketDate,
  makeBucketTickFormatter,
  spansMultipleYears,
} from '../chartFormatting'
import { makeYAxisTickFormatter } from '../formatYAxisTick'
import { yTicksForDomain } from '../yAxisTicks'
import {
  augmentRowsWithNoDataSegments,
  DOT_RADIUS,
  isGreyingEligible,
  LINE_STROKE_WIDTH,
  renderNoDataDotLines,
  renderNoDataGapLines,
} from '../noDataLine'
import {
  pickComparisonXTicks,
  StackedDateTick,
  X_AXIS_HEIGHT_STACKED,
} from '../stackedDateTick'
import { chartXAxisTicks } from '../xAxisTicks'
import { MetricUnit, SummaryMode } from '../types'
import type { ChartAnnotation, ChartSeries } from '../types'
import {
  ANNOTATION_AXIS_HEIGHT,
  useChartAnnotations,
} from '../useChartAnnotations'
import annotationStyles from '../chartAnnotations.module.css'

// ── Styling (owned by the chart, never a prop) ──────────────────────────────────
/** Dash pattern for a comparison-period line. */
const LINE_DASH = '4 2'
const BAR_RADIUS: [number, number, number, number] = [2, 2, 0, 0]

/** At/above this many x-axis ticks the axis is crowded, so year labels use the
 *  compact `'26` form; a roomier axis can spell out the full year. */
const ROOMY_TICK_MAX = 8

/** Per-day counts (Total, e.g. Posts) draw as bars; cumulative series (Latest,
 *  e.g. Followers) draw as a line. */
function isBarSeries(series: ChartSeries): boolean {
  return series.summaryMode === SummaryMode.TOTAL
}

export interface ComposedChartProps {
  /** Accessible name for the chart (e.g. "Posts and followers over time"). */
  ariaLabel: string
  height?: number
  startDate?: Date
  endDate?: Date
  /** Overlay shown when there's no data; the axes/grid stay visible. */
  emptyState?: ReactNode
  /** Show each series' icon in the tooltip. */
  showIconsInTooltip?: boolean
  /** Disable enter animations (used by the offscreen export snapshot). */
  disableAnimations?: boolean
  xAxisTickCount?: number
  /** X-axis shows only the comparison-period dates (Comparison view). */
  comparisonOnly?: boolean
  /**
   * Bucket size the series were aggregated into, driving the axis-tick and
   * tooltip date labels (a day, a week range, or a month). Defaults to `day`.
   * The caller buckets the data to match (see `bucketSeries`).
   */
  granularity?: BucketGranularity
  /** Left Y-axis label; defaults to the first (bar) series' label. */
  yAxisLabel?: string
  xAxisTicks?: number[]
  /**
   * Flag markers pinned to the time axis (see {@link ChartAnnotation}). The
   * category (band) x-axis positions a flag on the bucket nearest its date. The
   * "no data before" hatch is intentionally not drawn here: a composed view also
   * plots posts, which predate analytics collection, so the hatch would mislead.
   */
  annotations?: ChartAnnotation[]
  /** Fixed y-tick count that fills `height` evenly, bypassing the 48px
   *  fit-to-height cadence. The export snapshot sets this so its taller card
   *  shows the same tick count as the on-screen chart instead of many more. */
  yTickCount?: number
}

/**
 * The base composed bar + line chart: per-day counts (Total, e.g. Posts) drawn as
 * columns and cumulative series (Latest, e.g. Followers) drawn as a line, from
 * {@link ChartSeriesContext}. Two distinct base metrics split across dual axes
 * (bars left, line right). Presentational and style-owning — colour is never a
 * prop. Comparison overlays render as grouped lighter bars / a dashed line; a
 * Latest line bridges its null (computed/missing) days via `connectNulls`, which
 * still read as "No data available" in the tooltip. Keyboard-navigable via
 * Recharts `accessibilityLayer`, with a screen-reader summary + data table (via
 * ChartFrame).
 */
export function ComposedChart({
  ariaLabel,
  height = 250,
  startDate,
  endDate,
  emptyState,
  showIconsInTooltip = false,
  disableAnimations = false,
  xAxisTickCount,
  comparisonOnly = false,
  granularity = 'day',
  yAxisLabel,
  xAxisTicks,
  annotations = [],
  yTickCount,
}: ComposedChartProps): JSX.Element {
  const monthly = granularity === 'month'
  const series = useChartSeries()
  const {
    annotationPoints,
    activeAnnotationId,
    setActiveAnnotationId,
    activeAnnotation,
    showAnnotations,
    closeAnnotationOnGraphHover,
  } = useChartAnnotations(annotations)
  const {
    resolvedColors,
    noDataColor,
    surfaceColor,
    gridColor,
    neutralTextColor,
    subtleTextColor,
    cursor,
  } = useChartChrome(series)

  // Rows of observed values, before the no-data split. The Y-domain is derived
  // from these (the split adds no values outside the observed range), and its
  // floor feeds the leading-gap ramp — so it must be computed before augmenting.
  const { data: observed, comparisonDateByTimestamp } = useMemo(
    () => buildSeriesRows(series),
    [series],
  )

  // Group series by base metric: two distinct base metrics → dual axes
  // (bars on the left, the line on the right).
  const baseKeys = useMemo(() => {
    const seen = new Set<string>()
    const result: string[] = []
    for (const s of series) {
      const k = baseKey(s)
      if (!seen.has(k)) {
        seen.add(k)
        result.push(k)
      }
    }
    return result
  }, [series])
  const leftMetrics = useMemo(
    () => series.filter((s) => baseKey(s) === baseKeys[0]),
    [series, baseKeys],
  )
  const rightMetrics = useMemo(
    () =>
      baseKeys.length > 1
        ? series.filter((s) => baseKey(s) === baseKeys[1])
        : [],
    [series, baseKeys],
  )
  const useDualAxes = baseKeys.length === 2 && rightMetrics.length > 0

  const empty = emptyState != null
  const leftSeries = useDualAxes ? leftMetrics : series
  // A bar axis pins to a 0 baseline; a line axis pads/floats around its extent —
  // this drives both the domain padding and the y-tick baseline (zeroBaseline).
  const leftIsColumn = useMemo(() => leftSeries.some(isBarSeries), [leftSeries])
  const rightIsColumn = useMemo(
    () => rightMetrics.some(isBarSeries),
    [rightMetrics],
  )
  const yDomain = useMemo(
    () =>
      computeYDomain(observed, leftSeries, {
        column: leftIsColumn,
        stacked: false,
        empty,
      }),
    [observed, leftSeries, leftIsColumn, empty],
  )
  const yDomainRight = useMemo(
    (): [number, number | string] =>
      useDualAxes
        ? computeYDomain(observed, rightMetrics, {
            column: rightIsColumn,
            stacked: false,
            empty,
          })
        : [0, 'auto'],
    [observed, rightMetrics, rightIsColumn, useDualAxes, empty],
  )

  // Split each eligible (Latest, non-comparison) line — e.g. Followers — into its
  // observed colour line plus the grey `__gap` / `__gapMarker` / `__dot` layers, so
  // trailing/leading no-data days read the same as on the Visibility chart. Each
  // axis has its own floor for the leading-gap ramp.
  const leftFloor = typeof yDomain[0] === 'number' ? yDomain[0] : 0
  const rightFloor = typeof yDomainRight[0] === 'number' ? yDomainRight[0] : 0
  const data = useMemo(
    () =>
      augmentRowsWithNoDataSegments(observed, series, {
        leftFloor,
        rightFloor,
        useDualAxes,
        baseKeys,
      }),
    [observed, series, leftFloor, rightFloor, useDualAxes, baseKeys],
  )

  const isSinglePoint = data.length <= 1

  const comparisonXTicks = useMemo(
    () => pickComparisonXTicks(data, comparisonDateByTimestamp, xAxisTickCount),
    [data, comparisonDateByTimestamp, xAxisTickCount],
  )

  const xTicks = data.map((d) => d.timestamp)
  // Bucketed (week/month) views label every bucket — one tick per bar, so gaps stay
  // even and both end dates show; daily views thin to a legible set. In comparison
  // mode every bucket is aligned, so the same set also drives the stacked two-date
  // labels. (see chartXAxisTicks)
  const explicitXAxisTicks = chartXAxisTicks(granularity, xTicks, {
    callerTicks: xAxisTicks,
    comparisonTicks: comparisonXTicks,
    tickCount: xAxisTickCount,
  })

  const hasComparison = comparisonDateByTimestamp.size > 0

  // Spell out the year on every tick once the displayed dates cross a calendar
  // year — most often a comparison overlay against the prior year, where the
  // primary ("Mar '26") and comparison ("Aug '25") lines would otherwise be
  // ambiguous. Read from the range endpoints, the plotted buckets, and the
  // comparison dates — the dates actually labelled on the axis.
  const spansYears = useMemo(() => {
    const timestamps = data.map((d) => d.timestamp)
    if (startDate) timestamps.push(startDate.getTime())
    if (endDate) timestamps.push(endDate.getTime())
    for (const iso of comparisonDateByTimestamp.values()) {
      timestamps.push(new Date(iso).getTime())
    }
    return spansMultipleYears(timestamps)
  }, [data, startDate, endDate, comparisonDateByTimestamp])

  // Bucket-aware labels: a week by its start date ("Jun 1"), the day, or the month;
  // the tooltip header reads the full period the point represents. A crowded axis
  // (many ticks) or the stacked comparison labels use the compact `'26` year; a
  // roomy axis spells the full year out.
  const compactYear =
    hasComparison ||
    (explicitXAxisTicks?.length ?? data.length) > ROOMY_TICK_MAX
  const formatDateTick = useMemo(
    () =>
      makeBucketTickFormatter(granularity, {
        showYear: spansYears,
        compactYear,
      }),
    [granularity, spansYears, compactYear],
  )
  const formatTooltipDate = useMemo(
    () =>
      (timestampMs: number): string =>
        formatBucketDate(timestampMs, granularity),
    [granularity],
  )

  const hasStackedComparisonDates = hasComparison && !comparisonOnly
  const xAxisHeight =
    hasStackedComparisonDates && showAnnotations
      ? Math.max(X_AXIS_HEIGHT_STACKED, ANNOTATION_AXIS_HEIGHT)
      : hasStackedComparisonDates
        ? X_AXIS_HEIGHT_STACKED
        : showAnnotations
          ? ANNOTATION_AXIS_HEIGHT
          : undefined
  const effectiveXAxisHeight = xAxisHeight ?? DEFAULT_X_AXIS_HEIGHT

  // Both Y-axes take the same tick count, so the right-axis labels land on the
  // left-axis gridlines; the snapshot pins the count instead of fitting 48px.
  const { chartHeight, tickCount } =
    yTickCount != null
      ? { chartHeight: height, tickCount: yTickCount }
      : fitToGrid(height, effectiveXAxisHeight)
  const leftAxis = useMemo(
    () =>
      yTicksForDomain(yDomain, tickCount, {
        integer: leftMetrics[0]?.unit !== MetricUnit.PERCENTAGE,
        zeroBaseline: leftIsColumn,
      }),
    [yDomain, tickCount, leftMetrics, leftIsColumn],
  )
  const rightAxis = useMemo(
    () =>
      yTicksForDomain(yDomainRight, tickCount, {
        integer: rightMetrics[0]?.unit !== MetricUnit.PERCENTAGE,
        zeroBaseline: rightIsColumn,
      }),
    [yDomainRight, tickCount, rightMetrics, rightIsColumn],
  )

  const leftAxisLabel = yAxisLabel ?? leftMetrics[0]?.label
  const rightAxisLabel = useDualAxes ? rightMetrics[0]?.label : leftAxisLabel

  // Size each band to its own tick labels so the numbers stay 12px from the
  // rotated axis label whatever their width (see computeYAxisWidth).
  const leftAxisWidth = useMemo(
    () =>
      empty
        ? Y_AXIS_WIDTH
        : computeYAxisWidth(
            axisTickLabels(
              leftAxis.ticks,
              makeYAxisTickFormatter(
                leftAxis.domain,
                leftMetrics[0]?.unit,
                tickCount,
              ),
            ),
          ),
    [empty, leftAxis, leftMetrics, tickCount],
  )
  const rightAxisWidth = useMemo(
    () =>
      empty
        ? Y_AXIS_WIDTH
        : computeYAxisWidth(
            axisTickLabels(
              rightAxis.ticks,
              makeYAxisTickFormatter(
                rightAxis.domain,
                rightMetrics[0]?.unit,
                tickCount,
              ),
            ),
          ),
    [empty, rightAxis, rightMetrics, tickCount],
  )

  return (
    <ChartFrame
      ariaLabel={ariaLabel}
      monthly={monthly}
      emptyState={emptyState}
      height={chartHeight}
    >
      <RechartsComposedChart
        data={data}
        margin={CHART_MARGIN}
        accessibilityLayer
        aria-label={ariaLabel}
        onMouseMove={closeAnnotationOnGraphHover}
      >
        {/* Bind to the left axis: recharts' default yAxisId 0 is a hidden implicit
            axis, so without this the gridlines wouldn't sit on our ticks (dual-axis esp.). */}
        <CartesianGrid yAxisId="left" vertical={false} stroke={gridColor} />

        <XAxis
          dataKey="timestamp"
          // A category (band) axis centres each column — and its tick — in its own
          // slot, matching the Posts stacked-bar chart. (Bars on a numeric axis
          // sit off-centre from their ticks and clip at the edges.)
          type="category"
          ticks={explicitXAxisTicks}
          // Daily: recharts keeps both ends and drops only interior labels that
          // would collide on a narrow (mobile) axis (desktop fits them all).
          // Bucketed (week/month) and comparison views keep every tick — thinning
          // would strip labels from under bars, and comparison's formatter-less
          // ticks would mis-measure the raw timestamp.
          interval={
            hasComparison || granularity !== 'day' ? 0 : 'preserveStartEnd'
          }
          minTickGap={X_AXIS_MIN_TICK_GAP}
          tickFormatter={
            comparisonDateByTimestamp.size > 0 ? undefined : formatDateTick
          }
          tick={
            comparisonDateByTimestamp.size > 0
              ? (props) => (
                  <StackedDateTick
                    {...props}
                    color={neutralTextColor}
                    comparisonDateByTimestamp={comparisonDateByTimestamp}
                    comparisonOnly={comparisonOnly}
                    formatTick={formatDateTick}
                  />
                )
              : { fontSize: TICK_FONT_SIZE, fill: neutralTextColor, dy: 8 }
          }
          height={effectiveXAxisHeight}
          axisLine={{ stroke: gridColor }}
          tickLine={false}
          padding={{ left: X_AXIS_PADDING, right: X_AXIS_PADDING }}
        />

        <YAxis
          yAxisId="left"
          orientation="left"
          domain={leftAxis.domain}
          ticks={leftAxis.ticks}
          width={leftAxisWidth}
          tick={{ fontSize: TICK_FONT_SIZE, fill: subtleTextColor }}
          axisLine={false}
          tickLine={false}
          tickSize={0}
          tickMargin={Y_AXIS_TICK_MARGIN}
          tickFormatter={
            empty
              ? undefined
              : makeYAxisTickFormatter(
                  leftAxis.domain,
                  leftMetrics[0]?.unit,
                  tickCount,
                )
          }
        >
          {leftAxisLabel && (
            <Label
              value={leftAxisLabel}
              angle={-90}
              position="insideLeft"
              offset={Y_AXIS_LABEL_OFFSET}
              style={{
                fill: subtleTextColor,
                fontSize: TICK_FONT_SIZE,
                textAnchor: 'middle',
              }}
            />
          )}
        </YAxis>

        {useDualAxes && (
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={rightAxis.domain}
            ticks={rightAxis.ticks}
            width={rightAxisWidth}
            tick={{ fontSize: TICK_FONT_SIZE, fill: subtleTextColor }}
            axisLine={false}
            tickLine={false}
            tickSize={0}
            tickMargin={Y_AXIS_TICK_MARGIN}
            tickFormatter={
              empty
                ? undefined
                : makeYAxisTickFormatter(
                    rightAxis.domain,
                    rightMetrics[0]?.unit,
                    tickCount,
                  )
            }
          >
            {rightAxisLabel && (
              <Label
                value={rightAxisLabel}
                angle={90}
                position="insideRight"
                offset={Y_AXIS_LABEL_OFFSET}
                style={{
                  fill: subtleTextColor,
                  fontSize: TICK_FONT_SIZE,
                  textAnchor: 'middle',
                }}
              />
            )}
          </YAxis>
        )}

        <Tooltip
          content={(props) => (
            <ChartTooltipView
              {...props}
              showIcons={showIconsInTooltip}
              formatDate={formatTooltipDate}
              // Posts (Total) draw as hatched bars → hatched key; the followers
              // (Latest) line keeps the dashed key that mirrors its dashed line.
              comparisonSwatch={(entry) =>
                entry.summaryMode === SummaryMode.TOTAL ? 'hatched' : 'dashed'
              }
            />
          )}
          cursor={cursor}
          // Suppress the data tooltip (and its cursor) while a flag popover is
          // open so the two don't overlap.
          active={activeAnnotationId != null ? false : undefined}
        />

        {/* Grey no-data lines render before the series marks so they sit *under*
            them (draw order is z-order) — grey never shows over an observed
            segment. On the category axis the trailing hold runs across the
            plotted buckets, ending at the last bucket's centre. */}
        {renderNoDataGapLines(series, {
          noDataColor,
          surfaceColor,
          useDualAxes,
          baseKeys,
          disableAnimations,
        })}

        {series.map((s): JSX.Element => {
          const yAxisId =
            useDualAxes && baseKey(s) === baseKeys[1] ? 'right' : 'left'
          const isComparison = s.kind === 'comparison'

          if (isBarSeries(s)) {
            // Grouped bars come for free: two <Bar> on the same yAxisId render
            // side by side (this period vs comparison).
            return (
              <Bar
                key={s.id}
                dataKey={s.id}
                yAxisId={yAxisId}
                fill={resolvedColors[s.id]}
                radius={BAR_RADIUS}
                shape={
                  isComparison ? (
                    <ComparisonHatchBar radius={BAR_RADIUS} />
                  ) : undefined
                }
                isAnimationActive={!disableAnimations}
              />
            )
          }

          // An eligible cumulative line (e.g. Followers) breaks at nulls so the
          // grey no-data line drawn below shows through its missing days, matching
          // the Visibility chart. A comparison overlay bridges its own gaps instead
          // (one dashed trend). Filled boundary dots come from the `__dot` line.
          return (
            <Line
              key={s.id}
              type="monotone"
              dataKey={s.id}
              yAxisId={yAxisId}
              stroke={resolvedColors[s.id]}
              strokeWidth={LINE_STROKE_WIDTH}
              strokeDasharray={isComparison ? LINE_DASH : undefined}
              dot={isSinglePoint ? { r: DOT_RADIUS } : false}
              activeDot={{ r: DOT_RADIUS }}
              connectNulls={!isGreyingEligible(s)}
              isAnimationActive={!disableAnimations}
            />
          )
        })}

        {/* Filled dots at real collection points that border a gap or stand alone
            (a contiguous daily run gets none), on top of the colour line. */}
        {renderNoDataDotLines(series, {
          resolvedColors,
          useDualAxes,
          baseKeys,
        })}

        {/* Faint guide line(s) the active flag reveals, snapped to the bucket(s)
            the annotation's dates fall in. */}
        {showAnnotations &&
          activeAnnotation?.lineTimestamps.map((ts) => (
            <ReferenceLine
              key={`${activeAnnotation.id}-guide-${ts}`}
              className={annotationStyles.annotationReveal}
              yAxisId="left"
              x={snapToBucket(ts, xTicks)}
              stroke={neutralTextColor}
              strokeDasharray="3 3"
              strokeOpacity={0.5}
            />
          ))}

        {/* Flag chips. The carrier line is invisible — guide lines are drawn
            separately above so one flag can mark several dates. */}
        {showAnnotations &&
          annotationPoints.map((annotation) => (
            <ReferenceLine
              key={annotation.id}
              yAxisId="left"
              x={snapToBucket(annotation.ts, xTicks)}
              stroke="transparent"
              label={
                <ChartAnnotationFlag
                  ariaLabel={annotation.ariaLabel}
                  popoverContent={annotation.content}
                  stacked={hasStackedComparisonDates}
                  open={activeAnnotationId === annotation.id}
                  onOpenChange={(open) =>
                    setActiveAnnotationId((current) =>
                      open
                        ? annotation.id
                        : current === annotation.id
                          ? null
                          : current,
                    )
                  }
                />
              }
            />
          ))}
      </RechartsComposedChart>
    </ChartFrame>
  )
}
